import { getBreadCrumbSchema } from '../../../constants/componentStaticData';
import { handleTrack } from '../../../utils/tracker';
import { BREADCRUMB_TYPE, Breadcrumb } from '../../Atoms/BreadCrumb';
import { ButtonVariants } from '../../Atoms/Button';
import { SimpleSlider } from '../../Atoms/Carousel';
import { If } from '../../Atoms/Conditionals';
import { StickyCTA } from '../../CTA/StickyCTA';
import { REQUEST_DEMO_TEXT, requestDemo } from '../../CTA/constants';
import { TickMark } from '../../Icons';
import { Heading, HeadingType } from '../../Typography';
import { WebinarCard } from '../Common/WebinarCard';
import { WebinarLeadForm } from '../Common/WebinarLeadForm';
import { FORM_VALUE } from '../Common/WebinarLeadForm/FormData/data';
import { getShortDate } from '../Utils';
import { EVENT_TYPE } from '../Utils/constants';
import { IDETAIL_PAGE_DATA, IWEBINAR_DATA } from '../Utils/types';
import { WebinarVideoForm } from './WebinarVideoForm';
import { SpeakerCard } from './speaker-card';

const WebinarDetailPage = ({
  data,
}: {
  data: IDETAIL_PAGE_DATA;
}): JSX.Element => {
  const { webinarDetails, relatedWebinars } = data;
  const isPastWebinar =
    new Date(webinarDetails.airing_at) < new Date() ? true : false;

  const cardClick = (
    webinar: IWEBINAR_DATA,
    label: string,
    buttonText: string
  ) => {
    const redirectUrl = `${'/webinars/'}${webinar.slug}`;
    handleTrack({
      label: `${label}_${webinar.title}_${buttonText}`,
      url: redirectUrl,
    });

    window.open(redirectUrl);
  };

  const BREADCRUMB: Array<BREADCRUMB_TYPE> = [
    { title: 'Webinars', link: '/webinars' },
    { title: data.webinarDetails.title, link: null },
  ];

  return (
    <div data-testid="webinar-detailpage">
      <div className="wg-container hidden sm:block">
        <div className="mx-auto my-4 sm:mx-0">
          <Breadcrumb dataSet={BREADCRUMB} />
        </div>
      </div>
      <div className="wg-container flex items-start py-3 mb-10 gap-16 relative sm:flex-col-reverse sm:mb-4">
        <div className="w-1/2 sm:w-full">
          <div className="flex items-center mx-auto my-8 sm:hidden">
            <Breadcrumb dataSet={BREADCRUMB} />
          </div>
          <Heading
            content={webinarDetails.title}
            variant={HeadingType.h1}
            className="text-s-48 sm:text-s-28"
          />
          <p className="text-s-18 text-font-500 font-medium py-2 mb-2 sm:text-s-14">
            {webinarDetails.speaker &&
              `By ${
                webinarDetails.speaker.honorific_title
                  ? webinarDetails.speaker.honorific_title
                  : ''
              } ${webinarDetails.speaker?.name}, ${
                webinarDetails.speaker.designation
              } | `}{' '}
            {getShortDate(webinarDetails.airing_at)}
          </p>
          <hr />

          <div className="py-4">
            <If condition={!!webinarDetails.speaker?.name}>
              <SpeakerCard webinarDetails={webinarDetails} />
            </If>

            <div className="py-4">
              <Heading
                content="About the Webinar"
                variant={HeadingType.h2}
                className="text-s-24 font-bold sm:text-s-18 py-4"
              />

              <p className="text-font-200 text-base font-medium">
                {webinarDetails.description}
              </p>
            </div>

            {webinarDetails.key_points?.length ? (
              <div className="py-4">
                <Heading
                  content=" Things we will discuss"
                  variant={HeadingType.h2}
                  className="text-s-24 font-bold pb-4 sm:text-s-18"
                />

                {webinarDetails.key_points.map((list: string) => (
                  <div key={list} className="py-2 flex items-start gap-3">
                    <div className="pt-1">
                      <TickMark
                        colorCode="#4593FC"
                        fillColor="#E0EDFE"
                        width={18}
                        height={18}
                      />
                    </div>

                    <p className="text-font-200 text-base font-medium">
                      {list}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <>
          {isPastWebinar ? (
            webinarDetails.video_link ? (
              <div className="p-5 shadow-md rounded-lg sm:p-0 w-1/2 sm:w-full sticky top-0 sm:static">
                <WebinarVideoForm
                  webinarDetails={webinarDetails}
                  leadForm={{
                    eventType: EVENT_TYPE.PAST,
                    formTitle: 'Signup to watch our webinars for free',
                    initailFormData: FORM_VALUE,
                    buttonText: 'Watch now',
                    successData: {
                      icon: 'https://assets1.cleartax-cdn.com/cleartax/images/1675281704_congratulationsicon.png',
                      title: 'Congratulations! ',
                      desc: 'You can now access all our webinars for free',
                      buttonText: 'Okay',
                    },
                  }}
                />
              </div>
            ) : null
          ) : (
            <div className="p-5 shadow-md rounded-lg sm:p-0 w-1/2 sm:w-full sticky top-0 sm:static">
              <WebinarLeadForm
                webinarDetails={webinarDetails}
                eventType={EVENT_TYPE.UPCOMING}
                formTitle="Register for this webinar"
                initailFormData={FORM_VALUE}
                buttonText="Register now"
                successData={{
                  icon: 'https://assets1.cleartax-cdn.com/cleartax/images/1674553598_completedicon.png',
                  title: 'Thank you for registering',
                  desc: `You can join this webinar at ${
                    webinarDetails.airing_time
                  } on ${getShortDate(webinarDetails.airing_at)}`,
                }}
              />
            </div>
          )}
        </>
      </div>

      {relatedWebinars?.length ? (
        <div data-testid="related-webinars">
          <div className="wg-container py-10 sm:py-6">
            <Heading
              variant={HeadingType.h4}
              content="Related upcoming webinars"
              className="sm:text-s-28"
            />
            <p className="text-font-200 py-2">
              Our panelists are the most respected and knowledgeable in their
              respective fields
            </p>
          </div>

          <div className="mb-20 lg:wg-container">
            {relatedWebinars.length > 3 ? (
              <SimpleSlider>
                {relatedWebinars.map((upcomingData) => (
                  <WebinarCard
                    key={upcomingData.title}
                    data={upcomingData}
                    eventType={EVENT_TYPE.UPCOMING}
                    onClick={() =>
                      cardClick(
                        upcomingData,
                        'card_upcoming_webinar',
                        'Register_Now'
                      )
                    }
                  />
                ))}
              </SimpleSlider>
            ) : (
              <div className="wg-container">
                {relatedWebinars.map((upcomingData) => (
                  <div
                    key={upcomingData.title}
                    className="w-1/3 inline-block my-4 sm:w-full"
                  >
                    <WebinarCard
                      data={upcomingData}
                      eventType={EVENT_TYPE.UPCOMING}
                      onClick={() =>
                        cardClick(
                          upcomingData,
                          'card_upcoming_webinar',
                          'Register_Now'
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getBreadCrumbSchema(BREADCRUMB)),
        }}
      ></script>

      <StickyCTA
        buttonText={REQUEST_DEMO_TEXT}
        buttonStyle={ButtonVariants.bluefilled}
        link={requestDemo}
      />
    </div>
  );
};

export { WebinarDetailPage };
